import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card, Btn } from "../components/UI";
import {
  Sparkles, Send, Loader2, ChevronLeft, SlidersHorizontal, Target, MapPin, Tv,
  Wallet, CalendarRange, Users, ShieldCheck, Activity, ChevronDown, ChevronUp, Megaphone, Rocket, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { CreativeDraftsCard, LaunchChecklist, FundraiserCard, VoterFileCard } from "./agent/cards";

const SUGGESTIONS = [
  "Create a $50,000 CTV campaign for my race for the next 30 days.",
  "Reach voters in our district who care about property taxes and education.",
  "Create a campaign to introduce our candidate across the district.",
  "Build a fundraiser with a $25,000 goal and promote it with $20,000 in ads.",
  "We have $100,000 and 45 days before election day. Recommend a media plan.",
  "Use our voter file and reach those voters on CTV and online video.",
];

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString());

const renderMsg = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^#{1,4} (.*)$/gm, "<strong>$1</strong>")
    .replace(/^[-•] /gm, "• ");
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const PlanRow = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="flex items-start gap-2.5 py-2 border-b border-white/5 last:border-0" data-testid={testid}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${accent || "text-cyan-300"}`} />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xs text-slate-200 mt-0.5 break-words">{value || <span className="text-slate-600">Pending</span>}</div>
    </div>
  </div>
);

const SegmentPreviewCard = ({ audiences, onRemove }) => {
  if (!audiences || audiences.length === 0) return null;
  const totalReach = audiences.reduce((s, a) => s + Number(a.estimated_reach || 0), 0);
  const cpmAvg = (() => {
    const withCpm = audiences.filter((a) => Number(a.client_data_fee_cpm || 0) > 0);
    if (!withCpm.length) return null;
    return (withCpm.reduce((s, a) => s + Number(a.client_data_fee_cpm), 0) / withCpm.length).toFixed(2);
  })();
  return (
    <Card className="!p-4" data-testid="segment-preview-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-cyan-300" /> Segments in this plan ({audiences.length})
        </div>
        <div className="text-[10px] text-slate-500">
          {totalReach ? `${num(totalReach)} reach` : ""}{cpmAvg ? ` · avg CPM $${cpmAvg}` : ""}
        </div>
      </div>
      <div className="space-y-1.5">
        {audiences.map((a) => (
          <div key={a.name} className="rounded-md border border-white/5 bg-white/[0.02] p-2 flex items-start gap-2" data-testid={`segment-row-${(a.name || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold truncate">
                {a.provider || "Marketplace"}
              </div>
              <div className="text-xs text-slate-200 break-words leading-snug mt-0.5">{a.name}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-slate-500">
                {a.estimated_reach ? <span>Reach {num(a.estimated_reach)}</span> : null}
                {Number(a.client_data_fee_cpm || 0) > 0 && <span>CPM ${Number(a.client_data_fee_cpm).toFixed(2)}</span>}
                {a.ctv_eligible ? <span className="text-emerald-400">CTV ✓</span> : null}
              </div>
            </div>
            {audiences.length > 1 && (
              <button
                onClick={() => onRemove(a.name)}
                className="text-slate-500 hover:text-rose-300 text-lg leading-none shrink-0 px-1"
                title="Remove this segment"
                data-testid={`segment-remove-${(a.name || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}`}
              >×</button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-slate-500">Live from the TotalReach data catalog — edit before you approve the launch.</div>
    </Card>
  );
};

export default function CampaignAgent() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [activeEnt, setActiveEnt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [plan, setPlan] = useState({});
  const [activity, setActivity] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [launched, setLaunched] = useState(false);
  const [input, setInput] = useState(location.state?.prefill || "");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [launchRequest, setLaunchRequest] = useState(null);
  const [cardSetupUrl, setCardSetupUrl] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [creditQuote, setCreditQuote] = useState(null);
  const [applyCredit, setApplyCredit] = useState(true);
  const [uploadingVoterFile, setUploadingVoterFile] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [crossSellDismissed, setCrossSellDismissed] = useState(false);
  const bottomRef = useRef(null);

  const loadLaunchRequest = async () => {
    try {
      const { data } = await api.get(`/launch-approvals/by-session/${sid}`);
      setLaunchRequest(data.request);
      if (data.request?.status === "launched") setLaunched(true);
      return data.request;
    } catch { return null; }
  };

  useEffect(() => {
    api.get(`/agent/sessions/${sid}`).then((r) => {
      setSession(r.data);
      setMessages(r.data.messages || []);
      setPlan(r.data.plan || {});
      setActivity(r.data.activity || []);
      setCampaignId(r.data.campaign_id);
      setLaunched(r.data.status === "launched");
      if (["pending_approval", "launched"].includes(r.data.status)) loadLaunchRequest();
    }).catch(() => {
      toast.error("Agent session not found");
      navigate("/campaigns/new");
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get("launch_session")) {
      window.history.replaceState({}, "", `/campaign-agent/${sid}`);
      toast.success("Payment received — launching your campaign now");
      loadLaunchRequest();
    } else if (params.get("card_saved")) {
      window.history.replaceState({}, "", `/campaign-agent/${sid}`);
      toast.success("Card saved — it will be charged when your campaign is approved");
      setCardSetupUrl(null);
      loadLaunchRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, navigate]);

  useEffect(() => {
    if (!launchRequest || !["pending_approval", "approved_awaiting_payment", "paid"].includes(launchRequest.status)) return;
    const t = setInterval(loadLaunchRequest, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchRequest?.status]);

  useEffect(() => {
    if (!campaignId) { setReadiness(null); return; }
    api.get(`/agent/sessions/${sid}/launch-readiness`).then((r) => setReadiness(r.data)).catch(() => {});
  }, [campaignId, sid]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => {
    api.get("/political-os/my-entities").then(r => {
      const aid = r.data.active_entity_id;
      setActiveEnt((r.data.entities || []).find(e => e.id === aid) || null);
    }).catch(() => {});
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg, ts: new Date().toISOString() }]);
    try {
      const r = await api.post(`/agent/sessions/${sid}/message`, { text: msg });
      setMessages((m) => [...m, { role: "agent", content: r.data.reply, ts: new Date().toISOString() }]);
      setPlan(r.data.plan || {});
      setActivity((a) => [...a, ...(r.data.activity || [])]);
      if (r.data.campaign_id) setCampaignId(r.data.campaign_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The agent hit a snag — try again");
      setMessages((m) => [...m, { role: "agent", content: "Sorry — I hit a snag processing that. Could you try again?", ts: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const draftCreatives = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/draft-creatives`);
      setPlan(data.plan || {});
      setActivity((a) => [...a, { ts: new Date().toISOString(), text: "Drafted ad creative — display, CTV & audio" }]);
      toast.success("Creative drafts ready — review them in the panel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creative drafting failed");
    } finally { setDrafting(false); }
  };

  const uploadVoterFile = async (name, csvText) => {
    setUploadingVoterFile(true);
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/voter-file`, { name, csv_text: csvText });
      setPlan(data.plan || {});
      setActivity((a) => [...a, { ts: new Date().toISOString(), text: `Voter file "${name}" hashed & ${data.activated ? "activated" : "uploaded"}` }]);
      toast.success(data.activated ? "Voter file activated as a private audience" : "Voter file uploaded");
      send(`I uploaded my voter file "${name}" — ${Number(data.file.records).toLocaleString()} hashed identifiers${data.activated ? ", now activated as a private audience. Use it in the plan." : `. Note: ${data.note || ""}`}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voter file upload failed");
    } finally { setUploadingVoterFile(false); }
  };

  const switchEntity = async () => {
    try {
      const r = await api.post("/agent/sessions", {});
      navigate(`/campaign-agent/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start a new session"); }
  };

  useEffect(() => {
    if (!plan.budget) { setCreditQuote(null); return; }
    api.get(`/wallet/quote?budget=${Number(plan.budget)}`).then((r) => setCreditQuote(r.data)).catch(() => setCreditQuote(null));
  }, [plan.budget]);

  const launch = async () => {
    const creditNote = applyCredit && creditQuote?.credit_applied > 0
      ? ` ${usd(creditQuote.credit_applied)} of promotional credit will be applied first.` : "";
    if (!window.confirm(`Submit this campaign for approval? A TotalReach admin will review and release it — payment is collected by credit card at launch and includes a 3% card service fee.${creditNote}`)) return;
    setLaunching(true);
    setLaunchResult(null);
    try {
      if (!campaignId) {
        const d = await api.post(`/agent/sessions/${sid}/create-draft`);
        if (d.data.campaign_id) setCampaignId(d.data.campaign_id);
      }
      const { data } = await api.post(`/agent/sessions/${sid}/launch`, { apply_credit: applyCredit });
      setLaunchResult(data);
      if (data.campaign_id) setCampaignId(data.campaign_id);
      if (data.queued) {
        setCardSetupUrl(data.card_setup_url || null);
        setActivity((a) => [...a, ...(data.activity || [])]);
        await loadLaunchRequest();
        toast.success("Submitted for approval — an approver will release your campaign");
      } else if (!data.success) {
        toast.error("Launch stopped — see the checklist for what needs attention");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch submission failed");
    } finally { setLaunching(false); }
  };

  const payForLaunch = async () => {
    try {
      const { data } = await api.post(`/launch-approvals/${launchRequest.id}/pay`);
      if (data.checkout_url) window.open(data.checkout_url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the payment page");
    }
  };

  const removeAudience = async (name) => {
    const current = plan.audiences || [];
    const kept = current.filter((a) => a.name !== name).map((a) => a.name);
    if (kept.length === 0) {
      toast.error("Keep at least one segment — you can't clear the whole audience stack.");
      return;
    }
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/audiences`, { keep_names: kept });
      setPlan(data.plan || {});
      setActivity((a) => [...a, { ts: new Date().toISOString(), text: `Removed segment: ${name}` }]);
      toast.success("Segment removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove that segment");
    }
  };

  const channels = plan.channels || {};
  const channelText = Object.keys(channels).length
    ? Object.entries(channels).map(([c, p]) => `${c} ${p}%`).join(" · ")
    : null;
  const forecast = plan.forecast || {};
  const compliance = plan.compliance || {};
  const audiences = plan.audiences || [];
  const ready = !!(plan.objective && plan.budget && Object.keys(channels).length && plan.start_date && plan.end_date);
  const pendingApproval = !!launchRequest && ["pending_approval", "approved_awaiting_payment", "paid", "launch_failed"].includes(launchRequest.status);

  if (!session) return <div className="p-8 text-slate-400 text-sm">Loading Campaign Agent…</div>;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto h-[calc(100vh-1rem)] flex flex-col" data-testid="campaign-agent-page">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <button onClick={() => navigate("/campaigns/new")} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300" data-testid="back-to-choice">
            <ChevronLeft className="w-3.5 h-3.5" /> Create Campaign
          </button>
          <h1 className="font-display text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2 mt-0.5">
            <Sparkles className="w-5 h-5 text-cyan-300" /> TotalReach Campaign Agent
          </h1>
          {session?.entity_name && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-200" data-testid="agent-entity-chip">
                Campaign: {session.entity_name}
              </span>
              {activeEnt && session.entity_id && activeEnt.id !== session.entity_id && (
                <button onClick={switchEntity}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-200 hover:bg-amber-400/20 transition-colors"
                  data-testid="agent-switch-entity-btn">
                  Wrong campaign? Switch to {activeEnt.public_campaign_name || activeEnt.legal_org_name} →
                </button>
              )}
            </div>
          )}
        </div>
        {campaignId && (
          <Btn variant="secondary" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="open-pro-mode-link">
            <SlidersHorizontal className="w-4 h-4" /> Open Pro Mode
          </Btn>
        )}
      </div>

      <div className="flex-1 grid lg:grid-cols-3 gap-4 min-h-0">
        {/* Chat */}
        <Card className="lg:col-span-2 flex flex-col min-h-0 !p-0" padded={false}>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="agent-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-cyan-400/15 border border-cyan-400/20 text-cyan-50"
                    : "bg-white/[0.04] border border-white/10 text-slate-200"
                }`} data-testid={`agent-msg-${m.role}-${i}`}>
                  {renderMsg(m.content)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-4 py-2.5 text-xs bg-white/[0.04] border border-white/10 text-slate-400 inline-flex items-center gap-2" data-testid="agent-thinking">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> TotalReach is working — checking audiences, media and pricing…
                </div>
              </div>
            )}
            {messages.length <= 1 && !busy && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Try one of these</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="agent-suggestion"
                      className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors text-left">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-white/10 shrink-0">
            {plan.creative && (
              <div className="flex gap-2 flex-wrap mb-2" data-testid="agent-quick-links">
                <button onClick={() => navigate("/creatives")}
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-violet-400/25 text-violet-200 hover:bg-violet-400/10 transition-colors"
                  data-testid="chat-creatives-link">
                  <Palette className="w-3 h-3" /> Creative drafts →
                </button>
                {campaignId && (
                  <button onClick={() => navigate(`/campaigns/${campaignId}`)}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-slate-300 hover:border-cyan-400/30 transition-colors"
                    data-testid="chat-draft-link">
                    <SlidersHorizontal className="w-3 h-3" /> Open draft campaign →
                  </button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={2}
                placeholder="Tell TotalReach what you want your campaign to do…"
                data-testid="agent-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600"
              />
              <Btn onClick={() => send()} disabled={busy || !input.trim()} data-testid="agent-send-btn" className="self-end">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Btn>
            </div>
          </div>
        </Card>

        {/* Live plan */}
        <div className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          <Card data-testid="live-campaign-plan" className="!p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Campaign Plan</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${launched ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : pendingApproval ? "border-amber-400/30 bg-amber-400/10 text-amber-300" : ready ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-amber-400/30 bg-amber-400/10 text-amber-300"}`} data-testid="plan-status-pill">
                {launched ? "Launched" : pendingApproval ? "Pending approval" : campaignId ? "Draft created" : ready ? "Ready" : "Building"}
              </span>
            </div>
            <PlanRow icon={Target} label="Goal" value={plan.objective} testid="plan-goal" />
            <PlanRow icon={Users} label="Audience"
              value={audiences.length ? `${audiences.map((a) => a.name).join(", ")}${plan.audience_reach ? ` — ${num(plan.audience_reach)} est. reach` : ""}` : plan.audience_query}
              testid="plan-audience" />
            <PlanRow icon={MapPin} label="Geography" value={plan.geography} testid="plan-geography" />
            <PlanRow icon={Tv} label="Channels" value={channelText} accent={channels["Connected TV"] ? "text-emerald-300" : undefined} testid="plan-channels" />
            <PlanRow icon={Wallet} label="Budget" value={plan.budget ? usd(plan.budget) : null} testid="plan-budget" />
            <PlanRow icon={CalendarRange} label="Duration"
              value={plan.start_date ? `${plan.start_date} → ${plan.end_date}${plan.duration_days ? ` (${plan.duration_days} days)` : ""}` : null}
              testid="plan-duration" />
            <PlanRow icon={Activity} label="Forecast"
              value={forecast.impressions ? `${num(forecast.impressions)} impressions · ${num(forecast.estimated_reach)} reach · ${forecast.estimated_frequency}x freq — ${forecast.label}` : null}
              testid="plan-forecast" />
            <PlanRow icon={ShieldCheck} label="Compliance"
              value={compliance.items ? (compliance.ready ? "Ready" : `${compliance.items.length} item(s) pending`) : null}
              accent={compliance.ready ? "text-emerald-300" : "text-amber-300"} testid="plan-compliance" />
          </Card>

          <SegmentPreviewCard audiences={audiences} onRemove={removeAudience} />

          {channels["Connected TV"] && (
            <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/5 px-3.5 py-2.5 text-xs text-emerald-200 flex items-center gap-2" data-testid="ctv-hero-badge">
              <Tv className="w-4 h-4" /> Recommended: CTV + Digital — television-quality messaging extended across every screen.
            </div>
          )}

          {(campaignId || ready) && !launched && !pendingApproval && (
            <Card className="!p-4 border-cyan-400/25" data-testid="campaign-ready-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-cyan-300" /> {campaignId ? "Your campaign draft is ready" : "Your plan is ready"}</div>
              <p className="text-[11px] text-slate-500 mt-1">Draft creative, review the plan, then approve the launch — you stay in control.</p>
              {(campaignId ? readiness && !readiness.creative_ready : true) && (
                <div className="mt-2 rounded-md border border-amber-400/25 bg-amber-400/5 px-2.5 py-2 text-[11px] text-amber-200" data-testid="creative-warning-note">
                  No ad creatives are attached yet — the ad platform holds the live push until creatives are added. You can still submit for approval.
                </div>
              )}
              {creditQuote?.credit_applied > 0 && (
                <label className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-400/25 bg-emerald-400/5 px-3 py-2 text-[11px] text-emerald-200 cursor-pointer" data-testid="apply-credit-toggle">
                  <input type="checkbox" checked={applyCredit} onChange={(e) => setApplyCredit(e.target.checked)} className="mt-0.5" data-testid="apply-credit-checkbox" />
                  <span>
                    Apply <b>{usd(creditQuote.credit_applied)}</b> promotional credit ({creditQuote.items.map((i) => i.code).join(", ")}).
                    {creditQuote.credit_applied >= Number(plan.budget) ? " Covers the full media budget — no card charge." : " The card fee applies only to the remainder."}
                  </span>
                </label>
              )}
              <div className="flex flex-col gap-2 mt-3">
                {!plan.creative && (
                  <Btn variant="secondary" onClick={draftCreatives} disabled={drafting} data-testid="draft-creatives-btn">
                    {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}
                    {drafting ? "Drafting creative…" : "Draft creative (display · CTV · audio)"}
                  </Btn>
                )}
                {plan.creative && (
                  <Btn variant="secondary" onClick={() => navigate("/creatives")} data-testid="agent-view-creatives-btn">
                    <Palette className="w-4 h-4" /> View creative drafts
                  </Btn>
                )}
                <Btn onClick={launch} disabled={!ready || launching} data-testid="approve-launch-btn">
                  {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {launching ? "Launching…" : "Approve & Launch"}
                </Btn>
                {campaignId && (
                  <Btn variant="secondary" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="ready-open-pro">
                    <SlidersHorizontal className="w-4 h-4" /> Review in Pro Mode
                  </Btn>
                )}
              </div>
            </Card>
          )}

          {launchRequest && !launched && (
            <Card className={`!p-4 ${launchRequest.status === "rejected" ? "border-rose-400/30" : "border-amber-400/30"}`} data-testid="pending-approval-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-300" />
                {{
                  pending_approval: "Waiting for approval",
                  approved_awaiting_payment: "Approved — payment needed",
                  paid: "Payment received — launching",
                  launch_failed: "Launch needs attention",
                  rejected: "Launch request declined",
                }[launchRequest.status] || "Launch request"}
              </div>
              <div className="mt-2 text-[11px] text-slate-400 space-y-1" data-testid="launch-fee-breakdown">
                <div className="flex justify-between"><span>Media budget</span><b className="text-slate-200">{usd(launchRequest.budget)}</b></div>
                {launchRequest.credit_applied > 0 && (
                  <div className="flex justify-between text-emerald-300" data-testid="launch-credit-line"><span>Promotional credit</span><b>−{usd(launchRequest.credit_applied)}</b></div>
                )}
                <div className="flex justify-between"><span>Card service fee ({launchRequest.cc_fee_pct}%)</span><b className="text-slate-200">{usd(launchRequest.cc_fee_amount)}</b></div>
                <div className="flex justify-between text-cyan-300"><span>Total at launch</span><b>{launchRequest.total_with_fee <= 0 && launchRequest.credit_applied > 0 ? "$0 — covered by credit" : usd(launchRequest.total_with_fee)}</b></div>
              </div>
              {launchRequest.status === "pending_approval" && (
                <p className="text-[11px] text-slate-500 mt-2">
                  A platform admin or your client manager will review and release this campaign.
                  {launchRequest.total_with_fee <= 0 && launchRequest.credit_applied > 0 ? " Your promotional credit covers this launch — nothing will be charged." : launchRequest.card_on_file ? " Your card on file will be charged on approval." : ""}
                </p>
              )}
              {launchRequest.status === "pending_approval" && !launchRequest.card_on_file && cardSetupUrl && launchRequest.total_with_fee > 0 && (
                <Btn className="mt-3 w-full" onClick={() => window.open(cardSetupUrl, "_blank", "noopener")} data-testid="add-card-for-launch-btn">
                  <Wallet className="w-4 h-4" /> Add credit card
                </Btn>
              )}
              {launchRequest.status === "approved_awaiting_payment" && (
                <Btn className="mt-3 w-full" onClick={payForLaunch} data-testid="pay-for-launch-btn">
                  <Wallet className="w-4 h-4" /> Pay {usd(launchRequest.total_with_fee)} & go live
                </Btn>
              )}
              {launchRequest.status === "launch_failed" && (
                <p className="text-[11px] text-rose-300 mt-2">
                  {launchRequest.launch_error || "The ad-platform push failed."} An approver can retry from the Launch Approvals queue.
                </p>
              )}
              {launchRequest.status === "rejected" && (
                <p className="text-[11px] text-rose-300 mt-2">
                  {launchRequest.rejection_reason ? `Reason: ${launchRequest.rejection_reason}` : "Adjust the plan and press Approve & Launch to resubmit."}
                </p>
              )}
              <button onClick={loadLaunchRequest} className="mt-3 text-[11px] text-slate-500 hover:text-cyan-300" data-testid="refresh-launch-status-btn">
                Refresh status
              </button>
            </Card>
          )}

          {launched && !launchResult && (
            <Card className="!p-4 border-emerald-400/30" data-testid="launched-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2 text-emerald-200"><Rocket className="w-4 h-4 text-emerald-300" /> Campaign launched</div>
              <p className="text-[11px] text-slate-500 mt-1">This campaign is live on the ad platform. Track delivery in Reports.</p>
              <Btn variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/campaigns/${campaignId}`)}>Open campaign</Btn>
            </Card>
          )}

          {launchResult && (
            <Card className={`!p-4 ${launchResult.success ? "border-emerald-400/30" : "border-rose-400/30"}`} data-testid="launch-result-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2">
                <Rocket className={`w-4 h-4 ${launchResult.success ? "text-emerald-300" : "text-rose-300"}`} />
                {launchResult.queued ? "Submitted for approval" : launchResult.success ? "Launch complete" : "Launch needs attention"}
              </div>
              <LaunchChecklist steps={launchResult.steps || []} />
              {launchResult.success && (
                <Btn variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="launch-open-campaign">
                  Open campaign
                </Btn>
              )}
            </Card>
          )}

          <VoterFileCard wizard={plan.voter_file_wizard} voterFile={plan.voter_file} onUpload={uploadVoterFile} uploading={uploadingVoterFile} />
          <CreativeDraftsCard creative={plan.creative} />
          <FundraiserCard fundraiser={plan.fundraiser} />

          {launched && !plan.fundraiser && !crossSellDismissed && (
            <Card className="!p-4 border-amber-400/25" data-testid="fundraise-cross-sell-card">
              <div className="font-display font-semibold text-sm">Would you also like to raise money from this campaign?</div>
              <p className="text-[11px] text-slate-500 mt-1">TotalReach can create a donation page + fundraiser and connect it to this campaign in one step.</p>
              <div className="flex gap-2 mt-3">
                <Btn className="flex-1" onClick={() => { setCrossSellDismissed(true); send("Create a fundraising page for this campaign — pick a sensible goal based on the ad budget, build the landing page and connect it to the campaign."); }} data-testid="cross-sell-create-fundraiser">
                  Create fundraising page
                </Btn>
                <Btn variant="secondary" onClick={() => setCrossSellDismissed(true)} data-testid="cross-sell-dismiss">Not now</Btn>
              </div>
            </Card>
          )}

          <Card className="!p-4" data-testid="agent-activity-panel">
            <button onClick={() => setShowActivity(!showActivity)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 font-semibold" data-testid="toggle-activity">
              <span className="flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-cyan-300" /> What TotalReach did ({activity.length})</span>
              {showActivity ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showActivity && (
              <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
                {activity.map((a, i) => (
                  <div key={i} className="text-[11px] text-slate-400 flex items-start gap-1.5">
                    <span className="text-emerald-300 mt-px">✓</span> {a.text}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
